import { DataSource } from '@angular/cdk/collections';
import { HttpParams } from '@angular/common/http';
import { BehaviorSubject, finalize, Observable } from 'rxjs';
import { CitationColumn, ICitationDetails, ICitationDetailsV2 } from 'app/models/all-data.models';
import { IFilters } from 'app/models/filter.models';
import { AllDataService } from 'app/services/all-data.service';
import { format } from 'date-fns';

export class AllDataSource implements DataSource<ICitationDetailsV2> {
  private dataSubject = new BehaviorSubject<ICitationDetailsV2[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private totalElements = new BehaviorSubject<number>(0);
  public totalElements$ = this.totalElements.asObservable();
  private size = new BehaviorSubject<number>(0);
  public size$ = this.size.asObservable();

  public loading$ = this.loadingSubject.asObservable();

  constructor(private allDataService: AllDataService) {}

  connect(): Observable<ICitationDetailsV2[]> {
    return this.dataSubject.asObservable();
  }

  disconnect(): void {
    this.dataSubject.complete();
    this.loadingSubject.complete();
    this.totalElements.complete();
    this.size.complete();
  }

  mapData(data: ICitationDetailsV2[], activeColumns: string[], filter: IFilters): ICitationDetailsV2[] {
    if (activeColumns.includes(CitationColumn.CitationType) || activeColumns.includes(CitationColumn.OffenceCode)) {
      const rowsWithExtraFields: ICitationDetailsV2[] = [];
      for (let i = 0; i < data.length; i++) {
        const citation = data[i];
        let citationDetails = citation.violations;
        if (filter.violationFilter?.names?.length) {
          citationDetails = citationDetails.filter((x) => filter.violationFilter?.names?.includes(x.name));
        }
        if (filter.violationFilter?.policeCodes?.length) {
          citationDetails = citationDetails.filter((x) => filter.violationFilter?.policeCodes?.includes(x.policeCode));
        }
        if (citationDetails.length > 0) {
          const temp = citationDetails.map((x) => ({
            ...citation,
            offenceCode: x.policeCode,
            citationType: x.name,
            issueType: x.type,
            isDuplicated: true,
          }));
          temp[0].isDuplicated = false;
          rowsWithExtraFields.push(...temp);
        } else {
          rowsWithExtraFields.push(citation);
        }
      }

      return rowsWithExtraFields;
    }
    return data;
  }

  loadData(filter: IFilters, params: HttpParams, displayedColumns: string[]) {
    this.loadingSubject.next(true);
    this.allDataService
      .getAll(filter, params)
      .pipe(finalize(() => this.loadingSubject.next(false)))
      .subscribe((result) => {
        const data = result.content.map(
          ({
            id,
            referenceNumber,
            department,
            dateTime,
            month,
            dayNight,
            dayOfWeek,
            driver,
            location,
            officer,
            vehicle,
            stopDetails,
            violations,
          }: ICitationDetails) => {
            return {
              id,
              referenceNumber,
              department: { ...department },
              citationId: referenceNumber,
              time: format(new Date(dateTime), 'hh:mm'),
              month,
              date: format(new Date(dateTime), 'LL/dd/yyyy'),
              dayNight,
              dayOfWeek,
              driver: { ...driver },
              location: { ...location },
              officer: { ...officer },
              vehicle: { ...vehicle, vehicleLicPlate: vehicle.licPlate },
              stopDetails: { ...stopDetails },
              violations: [...violations],
              dateTime,
            };
          },
        );
        this.dataSubject.next(this.mapData(data, displayedColumns, filter));
        this.totalElements.next(result.totalElements);
        this.size.next(result.size);
      });
  }
}
